import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Formik } from 'formik';
import cx from 'classnames';

import { legacyApi } from '../../../../utils/api';
import BravelyEvents from '../../../../utils/events';

import { prepareSubmissionValues } from '../../shared/lib';
import { useStateValue } from '../../shared/state';
import { HANDLE_SUBMISSION } from '../../shared/reducer';
import { VIEW_PATH } from '../../shared/routes';

import FormFields from '../FormFields';
import FormAutosave from '../FormAutosave';
import FormControls from '../FormControls';

import styles from './styles';
import { camelize } from 'humps';
import BravelyData from 'utils/data';
import FlashMessage from 'shared/components/FlashMessage';

const DEFAULT_LEFT_OFFSET = '210px';

const isSessionSurvey = BravelyData('isSessionSurvey');

const handleSubmit = ({
  submission: { id },
  required,
  hidden,
  context,
  history,
  isNew,
  dispatch,
}) => (values) =>
  legacyApi.submissions
    .apiPostFormData(id, {
      submission: prepareSubmissionValues({ required, hidden, values }),
      context: context,
    })
    .then((response) => {
      dispatch({
        type: HANDLE_SUBMISSION,
        isNew: false,
        isReadOnly: response.parsed.submission.isReadOnly,
        data: response.parsed.submission.data,
        redirectTo:
          isNew && (context.forceRedirectTo || response.parsed.redirectTo)
            ? context.forceRedirectTo || response.parsed.redirectTo
            : VIEW_PATH,
      });
    });

// Render Form for NEW_PATH & EDIT_PATH
function Form({ location, history, disableFields = false }) {
  const [
    {
      submission,
      context,
      questions,
      required,
      hidden,
      conditions,
      isNew,
      initialValues,
      validationSchema,
      features,
      submitted,
    },
    dispatch,
  ] = useStateValue();

  const [leftOffset, setLeftOffset] = useState(DEFAULT_LEFT_OFFSET);

  useEffect(() => {
    if (disableFields) {
      const headerEl = document.getElementById('surveyHeader');
      if (headerEl) headerEl.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  BravelyEvents.once(
    BravelyEvents.EVENT.SessionDetailsCollapsePane,
    ({ collapsed, fullWidth, collapseWidth }) => {
      setLeftOffset(collapsed ? collapseWidth : fullWidth);
    }
  );

  return (
    <Formik
      enableReinitialize // Reflows form when it changes
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit({
        submission,
        required,
        hidden,
        context,
        history,
        isNew,
        dispatch,
      })}
    >
      {(props) => {
        return (
          <div
            className={cx(
              styles.container,
              styles[camelize(`${submission.name}Container`)]
            )}
          >
            {submitted && (
              <FlashMessage>{`${
                isSessionSurvey ? 'Survey' : 'Assessment'
              } Submitted Successfully`}</FlashMessage>
            )}
            <form onSubmit={props.handleSubmit}>
              <FormFields
                questions={questions}
                hidden={hidden}
                required={required}
                conditions={conditions}
                context={context}
                features={features}
                isNew={isNew}
                dispatch={dispatch}
                submission={submission}
                disableFields={disableFields}
                {...props}
              />
              {!disableFields && (
                <FormControls
                  isNew={isNew}
                  leftOffset={leftOffset}
                  submission={submission}
                />
              )}
              <FormAutosave
                enabled={features.autoSave}
                submission={submission}
              />
            </form>
          </div>
        );
      }}
    </Formik>
  );
}

Form.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(Form);
